/**
 * Tenant Admin → Packages page (Phase 21).
 *
 * A Package bundles catalog line items under a project type, so a
 * customer can start an estimate from "Full guest bath" instead of
 * picking fifteen options one at a time.
 *
 * Two things worth knowing about the data model here:
 *
 *  - Until this tenant saves a package of its own, it sees the PLATFORM
 *    defaults (the four validated bathroom packages). Saving one takes
 *    ownership of the whole list — the banner says so.
 *  - Referenced `material_id`s are not FK-checked on save (packages and
 *    the catalog are edited independently), so this screen flags dangling
 *    references instead. An apply skips them and reports it.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createPackage,
  listMaterials,
  listPackages,
  patchPackage,
  setPackageActive,
} from "@starz/shared/api/sdk.gen";
import type { ApiError, Material, Package } from "@starz/shared/api/types.gen";

const ROOM_TYPES = [
  "bathroom",
  "kitchen",
  "bedroom",
  "living_room",
  "garage",
  "whole_house",
  "other",
] as const;

interface DraftState {
  package_id: string;
  display_name: string;
  room_type: string;
  project_type: string;
  description: string;
  sort_order: number;
  lineItems: string[];
  addOns: string[];
}

const EMPTY_DRAFT: DraftState = {
  package_id: "",
  display_name: "",
  room_type: "bathroom",
  project_type: "",
  description: "",
  sort_order: 100,
  lineItems: [],
  addOns: [],
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function AdminPackages() {
  const [packages, setPackages] = useState<Package[]>([]);
  const [source, setSource] = useState<string>("tenant");
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Which package is being edited ("" = the create form, null = closed).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [pkgResult, matResult] = await Promise.all([
      listPackages(),
      listMaterials({ active: true }),
    ]);
    if ("error" in pkgResult) {
      setError(pkgResult.error.error.message);
    } else {
      setPackages(pkgResult.data);
      const src = pkgResult.meta?.source;
      setSource(typeof src === "string" ? src : "tenant");
      setError(null);
    }
    if (!("error" in matResult)) setMaterials(matResult.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const materialsById = useMemo(
    () => new Map(materials.map((m) => [m.material_id, m])),
    [materials],
  );

  /** Package items that no longer resolve to an active catalog material. */
  const danglingFor = useCallback(
    (pkg: Package): string[] =>
      [...pkg.line_item_material_ids, ...pkg.optional_add_on_material_ids].filter(
        (id) => !materialsById.has(id),
      ),
    [materialsById],
  );

  function openCreate() {
    setDraft(EMPTY_DRAFT);
    setEditingId("");
    setNotice(null);
    setError(null);
  }

  function openEdit(pkg: Package) {
    setDraft({
      package_id: pkg.package_id,
      display_name: pkg.display_name,
      room_type: pkg.room_type,
      project_type: pkg.project_type,
      description: pkg.description ?? "",
      sort_order: pkg.sort_order,
      lineItems: [...pkg.line_item_material_ids],
      addOns: [...pkg.optional_add_on_material_ids],
    });
    setEditingId(pkg.package_id);
    setNotice(null);
    setError(null);
  }

  function toggleItem(materialId: string, list: "lineItems" | "addOns") {
    setDraft((d) => {
      const other = list === "lineItems" ? "addOns" : "lineItems";
      const has = d[list].includes(materialId);
      return {
        ...d,
        [list]: has
          ? d[list].filter((id) => id !== materialId)
          : [...d[list], materialId],
        // An item is either applied or offered, never both — the API
        // rejects an overlap, so keep the UI from building one.
        [other]: d[other].filter((id) => id !== materialId),
      } as DraftState;
    });
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (draft.lineItems.length === 0) {
      setError("Pick at least one line item — an empty package isn't a choice.");
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);

    const body = {
      display_name: draft.display_name.trim(),
      room_type: draft.room_type,
      project_type: draft.project_type.trim() || slugify(draft.display_name),
      description: draft.description.trim() || null,
      line_item_material_ids: draft.lineItems,
      optional_add_on_material_ids: draft.addOns,
      sort_order: draft.sort_order,
    };

    const isNew = editingId === "";
    let result: { data: Package } | { error: ApiError };
    if (isNew) {
      const packageId = draft.package_id.trim() || slugify(draft.display_name);
      result = await createPackage({ body: { ...body, package_id: packageId } });
    } else {
      const existing = packages.find((p) => p.package_id === editingId);
      if (!existing) {
        setSaving(false);
        setError("That package is no longer here — reload and try again.");
        return;
      }
      result = await patchPackage({
        packageId: existing.package_id,
        ifMatchVersion: existing.version,
        body,
      });
    }

    setSaving(false);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setNotice(
      isNew
        ? `Created "${result.data.display_name}".`
        : `Saved "${result.data.display_name}".`,
    );
    setEditingId(null);
    void refresh();
  }

  async function onToggleActive(pkg: Package) {
    setBusy(pkg.package_id);
    setError(null);
    setNotice(null);
    const result = await setPackageActive({
      packageId: pkg.package_id,
      active: !pkg.active,
    });
    setBusy(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setNotice(
      pkg.active
        ? `Retired "${pkg.display_name}". Estimates that already used it are unaffected.`
        : `Restored "${pkg.display_name}".`,
    );
    void refresh();
  }

  return (
    <div className="mx-auto max-w-content px-6 py-12 md:px-12 md:py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Catalog
      </p>
      <h1 className="mb-3 font-serif text-4xl text-starz-navy">
        Project <em className="text-starz-gold">packages</em>.
      </h1>
      <div className="mb-6 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />

      <p className="mb-6 max-w-2xl text-sm text-warm-gray">
        A package is a starting bundle for one kind of job. Choosing it on the
        customer's wizard seeds their estimate with these line items — they can
        still change any of them.
      </p>

      {source === "platform" && (
        <p className="mb-4 rounded border border-starz-gold/30 bg-starz-cream px-3 py-2 text-sm text-starz-navy">
          These are the built-in defaults. Saving or creating a package here
          makes your own set — after that, only your packages are offered.
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="mb-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          {error}
        </p>
      )}
      {notice && (
        <p className="mb-4 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {notice}
        </p>
      )}

      {editingId === null && (
        <button
          type="button"
          onClick={openCreate}
          className="mb-8 min-h-[44px] rounded-lg bg-starz-navy px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white hover:bg-starz-navy/90"
        >
          New package
        </button>
      )}

      {editingId !== null && (
        <form
          onSubmit={onSave}
          className="mb-8 space-y-4 rounded-xl border border-starz-gold/20 bg-white p-4"
        >
          <h2 className="font-serif text-xl text-starz-navy">
            {editingId === "" ? "New package" : `Edit ${draft.display_name}`}
          </h2>

          <div className="flex flex-wrap gap-4">
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-warm-gray">Name</span>
              <input
                value={draft.display_name}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, display_name: e.target.value }))
                }
                required
                maxLength={120}
                className="min-h-[44px] rounded border border-starz-gold/30 px-3 py-2"
              />
            </label>

            <label className="flex flex-col text-sm">
              <span className="mb-1 text-warm-gray">Room</span>
              <select
                value={draft.room_type}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, room_type: e.target.value }))
                }
                className="min-h-[44px] rounded border border-starz-gold/30 px-3 py-2"
              >
                {ROOM_TYPES.map((rt) => (
                  <option key={rt} value={rt}>
                    {rt.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col text-sm">
              <span className="mb-1 text-warm-gray">Order</span>
              <input
                type="number"
                min={0}
                value={draft.sort_order}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, sort_order: Number(e.target.value) }))
                }
                className="min-h-[44px] w-24 rounded border border-starz-gold/30 px-3 py-2"
              />
            </label>

            {editingId === "" && (
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-warm-gray">ID (optional)</span>
                <input
                  value={draft.package_id}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, package_id: e.target.value }))
                  }
                  placeholder={slugify(draft.display_name) || "guest-bath"}
                  maxLength={64}
                  className="min-h-[44px] rounded border border-starz-gold/30 px-3 py-2"
                />
              </label>
            )}
          </div>

          <label className="flex flex-col text-sm">
            <span className="mb-1 text-warm-gray">
              One-line description (shown on the customer's card)
            </span>
            <input
              value={draft.description}
              onChange={(e) =>
                setDraft((d) => ({ ...d, description: e.target.value }))
              }
              maxLength={300}
              className="min-h-[44px] rounded border border-starz-gold/30 px-3 py-2"
            />
          </label>

          <fieldset>
            <legend className="mb-2 text-sm font-semibold text-starz-navy">
              Line items ({draft.lineItems.length}) — applied to the estimate
            </legend>
            <p className="mb-2 text-xs text-warm-gray">
              Tick an item to include it. Use “add-on” for anything that should
              be offered as an extra rather than counted in the total.
            </p>
            <ul className="max-h-80 space-y-1 overflow-y-auto rounded border border-starz-gold/15 p-2">
              {materials.map((m) => {
                const inLine = draft.lineItems.includes(m.material_id);
                const inAddOn = draft.addOns.includes(m.material_id);
                return (
                  <li
                    key={m.material_id}
                    className="flex items-center justify-between gap-3 rounded px-2 py-1 text-sm hover:bg-starz-cream/50"
                  >
                    <span className="min-w-0 truncate">
                      <span className="text-starz-navy">{m.display_name}</span>{" "}
                      <span className="text-xs text-warm-gray">
                        ({m.option_group_id ?? "no group"})
                      </span>
                      {!m.option_group_id && (
                        <span className="ml-2 text-xs text-rose-700">
                          can't be selected — needs an option group
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => toggleItem(m.material_id, "lineItems")}
                        aria-pressed={inLine}
                        className={
                          "min-h-[32px] rounded border px-2 py-1 text-xs " +
                          (inLine
                            ? "border-starz-gold bg-starz-cream font-semibold text-starz-navy"
                            : "border-starz-gold/30 text-warm-gray")
                        }
                      >
                        item
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleItem(m.material_id, "addOns")}
                        aria-pressed={inAddOn}
                        className={
                          "min-h-[32px] rounded border px-2 py-1 text-xs " +
                          (inAddOn
                            ? "border-starz-gold bg-starz-cream font-semibold text-starz-navy"
                            : "border-starz-gold/30 text-warm-gray")
                        }
                      >
                        add-on
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </fieldset>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white hover:bg-starz-navy/90 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save package"}
            </button>
            <button
              type="button"
              onClick={() => setEditingId(null)}
              className="min-h-[44px] rounded-lg px-5 py-3 text-sm font-semibold text-warm-gray"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-warm-gray">Loading…</p>
      ) : packages.length === 0 ? (
        <p className="text-sm text-warm-gray">
          No packages yet. Create one to offer it on the customer's wizard.
        </p>
      ) : (
        <ul className="space-y-3">
          {packages.map((pkg) => {
            const dangling = danglingFor(pkg);
            return (
              <li
                key={pkg.package_id}
                className="rounded-xl border border-starz-gold/20 bg-white p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-serif text-lg text-starz-navy">
                      {pkg.display_name}{" "}
                      {!pkg.active && (
                        <span className="align-middle text-xs font-sans uppercase tracking-widest text-warm-gray">
                          retired
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-warm-gray">
                      {pkg.room_type.replace(/_/g, " ")} · {pkg.project_type} ·{" "}
                      {pkg.line_item_material_ids.length} items
                      {pkg.optional_add_on_material_ids.length > 0 &&
                        ` · ${pkg.optional_add_on_material_ids.length} add-on`}
                      {" · order "}
                      {pkg.sort_order}
                    </p>
                    {pkg.description && (
                      <p className="mt-1 text-sm text-warm-gray">{pkg.description}</p>
                    )}
                    {dangling.length > 0 && (
                      <p className="mt-2 text-xs text-rose-700">
                        {dangling.length} item
                        {dangling.length === 1 ? "" : "s"} no longer in the
                        catalog ({dangling.join(", ")}) — these are skipped when a
                        customer applies this package.
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => openEdit(pkg)}
                      className="min-h-[44px] rounded-lg border border-starz-gold/30 px-4 py-2 text-sm font-semibold text-starz-navy hover:bg-starz-cream"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void onToggleActive(pkg)}
                      disabled={busy === pkg.package_id}
                      className="min-h-[44px] rounded-lg border border-starz-gold/30 px-4 py-2 text-sm font-semibold text-warm-gray hover:bg-starz-cream disabled:opacity-50"
                    >
                      {pkg.active ? "Retire" : "Restore"}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
